import { useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';

import UserCreditsByProgramEnrollmentId from 'bundles/enterprise-legacy-learner-home/queries/UserCreditsByProgramEnrollmentId.graphql';
import type {
  UserCreditsByProgramEnrollmentIdQuery,
  UserCreditsByProgramEnrollmentIdQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/UserCreditsByProgramEnrollmentId';

export type UserCredits = {
  coursesRemaining: number;
};

type UseProgramUserCreditsReturn = {
  loading: boolean;
  error?: ApolloError;
  userCredits: UserCredits | undefined;
};

/**
 * Hook for fetching program user credits using GraphQL
 *
 * @param userId - The user ID
 * @param programId - The program ID to fetch user credits for
 * @returns Object containing loading state, error, and user credits data
 */
export const useProgramUserCredits = (
  userId: number | null | undefined,
  programId: string | null | undefined
): UseProgramUserCreditsReturn => {
  const programEnrollmentId = userId && programId ? { userId, programId } : null;

  const { loading, error, data } = useQuery<
    UserCreditsByProgramEnrollmentIdQuery,
    UserCreditsByProgramEnrollmentIdQueryVariables
  >(UserCreditsByProgramEnrollmentId, {
    variables: { programEnrollmentId: programEnrollmentId! },
    context: { clientName: 'gatewayGql' },
    skip: !programEnrollmentId,
  });

  const rawUserCredits = data?.ProgramUserCredits?.queryByProgramEnrollmentId;
  const userCredits = rawUserCredits
    ? {
        coursesRemaining: rawUserCredits.coursesRemaining || 0,
      }
    : undefined;

  return {
    loading,
    error,
    userCredits,
  };
};
